import { startOfDay } from "date-fns";
import { useMemo } from "react";
import { useGetSubTopicEvents } from "../../../events/api/useGetSubTopicEvents";
import { RECENT_ACTIVITIES_LIMIT, useGetRecentActivities } from "../../api/useGetRecentActivities";
import { useGetSubTopicIncidents } from "../../api/useGetSubTopicIncidents";
import { useGetSubTopicQuotes } from "../../api/useGetSubTopicQuotes";
import { groupQuotesByStatus } from "../../utils/groupQuotesByStatus";

import { useHistoryDrawer } from "../HistoryDrawer/useHistoryDrawer";

export function isEventTodayOrUpcoming(event: { date: string | Date; end_date?: string | Date | null }) {
  const targetDate = new Date(event.end_date ?? event.date);
  return targetDate >= startOfDay(new Date());
}

type Props = {
  subTopicId: string;
  onNavigateToJournal?: () => void;
};

export function useActivitiesTab(props: Props) {
  const historyDrawer = useHistoryDrawer();

  const {
    data: recentActivitiesData,
    isLoading: activitiesLoading,
  } = useGetRecentActivities({ subTopicId: props.subTopicId }, RECENT_ACTIVITIES_LIMIT);

  const {
    data: events,
    isLoading: eventsLoading,
  } = useGetSubTopicEvents(props.subTopicId);

  const {
    data: incidents,
    isLoading: incidentsLoading,
  } = useGetSubTopicIncidents(props.subTopicId);

  const { data: quotes } = useGetSubTopicQuotes(props.subTopicId);

  const upcomingEvents = useMemo(() => {
    if (!events) return [];
    return events.filter(isEventTodayOrUpcoming);
  }, [events]);

  const ongoingIncidents = useMemo(() => {
    if (!incidents) return [];
    return incidents.filter(
      (item) => item.status === 'in_progress' || (item.status as string) === 'open'
    );
  }, [incidents]);

  const pendingQuotes = useMemo(
    () => groupQuotesByStatus(quotes ?? []).find((group) => group.status === 'pending')?.quotes ?? [],
    [quotes]
  );

  const totalActivitiesCount = recentActivitiesData?.total ?? 0;

  return {
    recentActivities: recentActivitiesData?.items ?? [],
    hasMoreActivities: totalActivitiesCount > RECENT_ACTIVITIES_LIMIT,
    totalActivitiesCount,
    activitiesLoading,
    events: upcomingEvents,
    eventsLoading,
    ongoingIncidents,
    incidentsLoading,
    pendingQuotes,
    handleSeeMore: props.onNavigateToJournal ?? historyDrawer.handleOpen,
    historyDrawer,
  };
}
